import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'

// =======================================================================
// First-Principles Library — manages the reusable building blocks of
// Wave 3's estimating engine: Resource Rates (time-boxed pricing),
// Project Calendars (working-hours basis), Crews (composition of
// resources), and the Productivity Library (labour/equipment output
// standards) with their adjustment Factors.
//
// This page performs ZERO financial calculation. Every derived number
// shown here (e.g. a crew's cost per shift) comes directly from the
// existing database functions/views (resolve_crew_cost_per_shift,
// first_principles_rate_breakdown) — never computed in React.
// =======================================================================

const RESOURCE_TYPES = [
  { value: 'material', label: 'Material' },
  { value: 'labour', label: 'Labour' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontract', label: 'Subcontract' },
]

export default function FirstPrinciplesLibraryPage() {
  const { tenderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Back goes to the page the user came from (for example Rate Build-Up), not always to the tender.
  // If this page was opened directly (new tab, pasted link) there is no earlier page inside the app,
  // so it falls back to the tender instead of doing nothing or leaving the app.
  function goBack() {
    if (location.key && location.key !== 'default') navigate(-1)
    else navigate(`/estimation/tender/${tenderId}`)
  }
  const [tab, setTab] = useState('calendars')
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const TABS = [
    { id: 'calendars', label: 'Calendars' },
    { id: 'resources', label: 'Resource Rates' },
    { id: 'costing-profiles', label: 'Costing Profiles' },
    { id: 'crews', label: 'Crews' },
    { id: 'productivity', label: 'Productivity Library' },
    { id: 'exchange-rates', label: 'Exchange Rates' },
  ]

  return (
    <div>
      <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, color: colors.textSecondary }}>
        &larr; Back
      </button>

      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>
        First-Principles Library
      </h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Resource Rate &rarr; Calendar &rarr; Crew &rarr; Productivity &rarr; Factor — reusable building blocks for First-Principles Rate Build-Up
      </p>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${colors.border}` }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 16px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
              color: tab === t.id ? colors.primary : colors.textSecondary,
              borderBottom: tab === t.id ? `2px solid ${colors.primary}` : '2px solid transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'calendars' && <CalendarsTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
      {tab === 'resources' && <ResourceRatesTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
      {tab === 'costing-profiles' && <CostingProfilesTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
      {tab === 'crews' && <CrewsTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
      {tab === 'productivity' && <ProductivityTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
      {tab === 'exchange-rates' && <ExchangeRatesTab tenderId={tenderId} onError={setError} onSuccess={setSuccessMsg} />}
    </div>
  )
}

// =======================================================================
// CALENDARS
// =======================================================================
// Calendar value rules. The CHECK constraints on estimation.project_calendar are the authoritative protection;
// the checks below only give the user a clear message before the request is sent (and translate the database error if it ever fires).
function validateCalendarValues(hours, shifts, days) {
  const h = Number(hours), s = Number(shifts), d = Number(days)
  if (hours === '' || !Number.isFinite(h) || h <= 0 || h > 24) return 'Hours per shift must be between 0 and 24.'
  if (shifts === '' || !Number.isFinite(s) || s <= 0) return 'Shifts per day must be greater than 0.'
  if (h * s > 24 + 1e-9) return 'Working hours per day cannot exceed 24.'
  if (days === '' || !Number.isFinite(d) || d < 1 || d > 7) return 'Working days per week must be between 1 and 7.'
  return null
}

function calendarDbErrorMessage(error, hours, shifts, days) {
  const text = `${error?.message || ''} ${error?.details || ''}`
  const isRangeError = error?.code === '23514' || /project_calendar_[a-z]+_chk/.test(text)
  if (!isRangeError) return error?.message || 'Could not create the calendar.'
  return validateCalendarValues(hours, shifts, days)
    || (/project_calendar_shifts_chk/.test(text) && 'Shifts per day must be greater than 0.')
    || (/project_calendar_days_chk/.test(text) && 'Working days per week must be between 1 and 7.')
    || (/project_calendar_daily_chk/.test(text) && 'Working hours per day cannot exceed 24.')
    || (/project_calendar_hours_chk/.test(text) && 'Hours per shift must be between 0 and 24.')
    || 'The calendar values are outside the allowed ranges. Please check them and try again.'
}

function CalendarsTab({ tenderId, onError, onSuccess }) {
  const [calendars, setCalendars] = useState(null)
  const [name, setName] = useState('')
  const [hoursPerShift, setHoursPerShift] = useState('8')
  const [shiftsPerDay, setShiftsPerDay] = useState('1')
  const [workingDaysPerWeek, setWorkingDaysPerWeek] = useState('6')
  const [isDefault, setIsDefault] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('project_calendar').select('*').eq('tender_id', tenderId).order('created_at')
    if (error) onError(error.message)
    else setCalendars(data)
  }, [tenderId, onError])

  useEffect(() => { load() }, [load])

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    if (!name.trim()) { onError('Name is required.'); return }
    const problem = validateCalendarValues(hoursPerShift, shiftsPerDay, workingDaysPerWeek)
    if (problem) { onError(problem); return }
    const { error } = await supabase.from('project_calendar').insert({
      tender_id: tenderId, name, hours_per_shift: Number(hoursPerShift),
      shifts_per_day: Number(shiftsPerDay), working_days_per_week: Number(workingDaysPerWeek), is_default: isDefault,
    })
    if (error) onError(calendarDbErrorMessage(error, hoursPerShift, shiftsPerDay, workingDaysPerWeek))
    else {
      onSuccess(`Calendar "${name}" created.`)
      setName(''); setHoursPerShift('8'); setShiftsPerDay('1'); setWorkingDaysPerWeek('6'); setIsDefault(false)
      await load()
    }
  }

  return (
    <div>
      <form onSubmit={handleAdd} noValidate style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} required style={{ width: 200 }} placeholder="Standard 8hr shift" /></Field>
        <Field label="Hours/Shift"><Input type="number" step="any" min="0.01" max="24" value={hoursPerShift} onChange={(e) => setHoursPerShift(e.target.value)} required style={{ width: 100 }} /></Field>
        <Field label="Shifts/Day"><Input type="number" step="any" min="0.01" value={shiftsPerDay} onChange={(e) => setShiftsPerDay(e.target.value)} required style={{ width: 100 }} /></Field>
        <Field label="Working Days/Week"><Input type="number" step="any" min="1" max="7" value={workingDaysPerWeek} onChange={(e) => setWorkingDaysPerWeek(e.target.value)} required style={{ width: 130 }} /></Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.textSecondary }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Default
        </label>
        <Button type="submit">+ Add Calendar</Button>
      </form>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {calendars === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={4} /></div>}
        {calendars?.length === 0 && <div style={{ padding: 24 }}><EmptyState title="No calendars yet" description="Create at least one calendar before defining a crew." /></div>}
        {calendars?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th>Name</Th><Th align="right">Hours/Shift</Th><Th align="right">Shifts/Day</Th><Th align="right">Days/Week</Th><Th>Default</Th>
            </tr></thead>
            <tbody>
              {calendars.map((c) => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Td>{c.name}</Td>
                  <Td align="right">{formatNumber(c.hours_per_shift)}</Td>
                  <Td align="right">{formatNumber(c.shifts_per_day)}</Td>
                  <Td align="right">{formatNumber(c.working_days_per_week)}</Td>
                  <Td>{c.is_default ? '✓' : ''}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// =======================================================================
// RESOURCE RATES — time-boxed pricing per resource_library entry
// =======================================================================
// Turns a database error from the resource-rate functions into a sentence the user can
// act on. Anything not recognised is shown exactly as the database wrote it (already readable).
function describeRateError(error) {
  const msg = (error && error.message) || ''
  if ((error && error.code === '23P01') || /resource_rate_no_overlap|exclusion constraint/i.test(msg)) {
    return 'this would put two rates in force on the same day for this resource. Choose a start date that no other rate of this resource already uses, or edit that rate instead.'
  }
  if (/does not exist|no longer exists/i.test(msg)) {
    return 'this rate no longer exists (it may have been deleted by someone else). The list has been refreshed.'
  }
  return msg
}

function ResourceRatesTab({ tenderId, onError, onSuccess }) {
  const [resources, setResources] = useState([])
  const [rates, setRates] = useState(null)
  const [resourceLibraryId, setResourceLibraryId] = useState('')
  const [rate, setRate] = useState('')
  const [currency, setCurrency] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [source, setSource] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editRate, setEditRate] = useState('')
  const [editCurrency, setEditCurrency] = useState('')
  const [editEffectiveFrom, setEditEffectiveFrom] = useState('')
  const [editSource, setEditSource] = useState('')

  // Tender-scoped: both reads explicitly filtered to tenderId -- never
  // left to RLS alone for page-level scoping.
  const load = useCallback(async () => {
    if (!tenderId) return
    const [resRes, rateRes] = await Promise.all([
      supabase.from('resource_library').select('id, code, description, resource_type, unit').eq('is_active', true).eq('tender_id', tenderId).order('code'),
      supabase.from('resource_rate').select('*').eq('tender_id', tenderId).order('effective_from', { ascending: false }),
    ])
    if (resRes.error) return onError(resRes.error.message)
    if (rateRes.error) return onError(rateRes.error.message)
    setResources(resRes.data)
    setRates(rateRes.data)
  }, [tenderId, onError])

  // Clear immediately on Tender switch so a stale Resource selection
  // from the previous Tender can never be submitted while the new
  // load() is still in flight.
  useEffect(() => {
    setResources([]); setRates(null); setResourceLibraryId('')
    load()
  }, [tenderId, load])

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    const currencyValue = currency.trim().toUpperCase()
    if (!currencyValue) { onError('Currency is required.'); return }

    // Creation is owned entirely by the database (create_resource_rate
    // RPC) -- it atomically closes whatever existing open-ended period
    // this new effective_from supersedes (and, for a backdated entry,
    // closes the NEW row at the next later existing period) before
    // inserting. This is a single owner for the close+insert mutation;
    // no equivalent logic is duplicated here in React.
    const { error } = await supabase.rpc('create_resource_rate', {
      p_tender_id: tenderId,
      p_resource_library_id: resourceLibraryId,
      p_rate: Number(rate),
      p_currency: currencyValue,
      p_effective_from: effectiveFrom,
      p_source: source || null,
    })
    if (error) onError(`Could not save rate — ${describeRateError(error)}`)
    else {
      onSuccess('Resource rate saved.')
      setRate(''); setCurrency(''); setEffectiveFrom(''); setSource('')
      await load()
    }
  }

  function startEdit(r) {
    setEditingId(r.id)
    setEditRate(String(r.rate))
    setEditCurrency(r.currency || '')
    setEditEffectiveFrom(r.effective_from)
    setEditSource(r.source || '')
  }

  async function handleSaveEdit(rateId) {
    onError(null)
    const editCurrencyValue = editCurrency.trim().toUpperCase()
    if (!editCurrencyValue) { onError('Currency is required.'); return }

    // Every edit — whether or not effective_from actually changed — goes
    // through edit_resource_rate(), the single authoritative database
    // mutation path for this table (per the "one owner" design). The
    // RPC performs a full temporal rebuild of the resource's entire rate
    // sequence (not a patch on just this row), so it is correct and
    // safe to call even when only rate/currency/source changed: the
    // rebuild is a no-op on effective_to values when effective_from
    // didn't move.
    const { error } = await supabase.rpc('edit_resource_rate', {
      p_rate_id: rateId,
      p_rate: Number(editRate),
      p_currency: editCurrencyValue,
      p_effective_from: editEffectiveFrom,
      p_source: editSource || null,
    })
    if (error) onError(`Could not update rate — ${describeRateError(error)}`)
    else {
      onSuccess('Resource rate updated.')
      setEditingId(null)
      await load()
    }
  }

  async function handleDelete(rateId) {
    onError(null)
    // Deletion goes through delete_resource_rate(): it checks tender access and the
    // FirstPrinciples.Manage permission, and when the CURRENT (open-ended) rate is removed
    // it reopens the previous rate so the history is not left with no rate in force.
    // The table is no longer deleted from directly.
    const { error } = await supabase.rpc('delete_resource_rate', { p_rate_id: rateId })
    if (error) {
      onError(`Could not delete rate — ${describeRateError(error)}`)
      if (/does not exist|no longer exists/i.test(error.message || '')) await load() // already gone: refresh the list
    } else {
      onSuccess('Resource rate deleted.')
      await load()
    }
  }

  return (
    <div>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Resource">
          <Select value={resourceLibraryId} onChange={(e) => setResourceLibraryId(e.target.value)} required style={{ width: 240 }}>
            <option value="" disabled>Select resource…</option>
            {resources.map((r) => <option key={r.id} value={r.id}>{r.code} — {r.description} ({r.resource_type})</option>)}
          </Select>
        </Field>
        <Field label="Rate"><Input type="number" step="any" min="0" value={rate} onChange={(e) => setRate(e.target.value)} required style={{ width: 110 }} /></Field>
        <Field label="Currency">
          <input
            list="currency-suggestions-resource-rate"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            onBlur={(e) => setCurrency(e.target.value.toUpperCase())}
            required
            placeholder="e.g. SAR"
            style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13 }}
          />
          <datalist id="currency-suggestions-resource-rate">
            {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Effective From"><Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required style={{ width: 160 }} /></Field>
        <Field label="Source"><Input value={source} onChange={(e) => setSource(e.target.value)} style={{ width: 200 }} placeholder="Supplier quote, market survey..." /></Field>
        <Button type="submit">+ Add Rate</Button>
      </form>
      <p style={{ fontSize: 12, color: colors.textMuted, marginTop: -10, marginBottom: 16 }}>
        A new rate automatically starts the day the previous one for the same resource ends — periods cannot overlap (enforced by the database).
      </p>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {rates === null && <div style={{ padding: 16 }}><SkeletonRows rows={3} cols={4} /></div>}
        {rates?.length === 0 && <div style={{ padding: 24 }}><EmptyState title="No resource rates yet" /></div>}
        {rates?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th>Resource</Th><Th align="right">Rate</Th><Th>Currency</Th><Th>Effective From</Th><Th>Effective To</Th><Th>Source</Th><Th></Th>
            </tr></thead>
            <tbody>
              {rates.map((r) => {
                const res = resources.find((x) => x.id === r.resource_library_id)
                const isEditing = editingId === r.id
                if (isEditing) {
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}`, background: '#FAFBFC' }}>
                      <Td>{res ? `${res.code} — ${res.description}` : r.resource_library_id}</Td>
                      <Td align="right"><Input type="number" step="any" min="0" value={editRate} onChange={(e) => setEditRate(e.target.value)} style={{ width: 90, fontSize: 13 }} /></Td>
                      <Td>
                        <input
                          list="currency-suggestions-resource-rate-edit"
                          value={editCurrency}
                          onChange={(e) => setEditCurrency(e.target.value)}
                          onBlur={(e) => setEditCurrency(e.target.value.toUpperCase())}
                          style={{ width: 80, padding: '4px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13 }}
                        />
                        <datalist id="currency-suggestions-resource-rate-edit">
                          {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </Td>
                      <Td><Input type="date" value={editEffectiveFrom} onChange={(e) => setEditEffectiveFrom(e.target.value)} style={{ width: 150, fontSize: 13 }} /></Td>
                      <Td muted>{r.effective_to || 'Ongoing'}</Td>
                      <Td><Input value={editSource} onChange={(e) => setEditSource(e.target.value)} style={{ width: 180, fontSize: 13 }} /></Td>
                      <Td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => handleSaveEdit(r.id)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                        </div>
                      </Td>
                    </tr>
                  )
                }
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td>{res ? `${res.code} — ${res.description}` : r.resource_library_id}</Td>
                    <Td align="right">{formatNumber(r.rate)}</Td>
                    <Td>{r.currency}</Td>
                    <Td>{r.effective_from}</Td>
                    <Td muted>{r.effective_to || 'Ongoing'}</Td>
                    <Td muted>{r.source || '—'}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => startEdit(r)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                        <button onClick={() => handleDelete(r.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                      </div>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// =======================================================================
// CREWS — composition of resources + calendar
// =======================================================================
// =======================================================================
// EXCHANGE RATES — the authoritative FX source consumed by
// resolve_fx_rate() (estimation schema). Global, not tender-scoped — same
// pattern as Resource Rates above: a rate here applies to every Tender
// that needs that currency pair, resolved per-line against each
// Revision's own pricing_date. This tab performs ZERO FX math; it only
// lists/creates/edits/deletes rows in estimation.exchange_rate. The
// financial engine (rate_buildup_line_cost_base) is the sole consumer
// that actually applies these rates to a calculation.
// =======================================================================
// Suggestions only -- From/To Currency below accept ANY typed value (any
// ISO 4217 code), not just these. Same list as TenderWorkspacePage.jsx's
// CURRENCY_SUGGESTIONS for consistency; duplicated locally rather than
// imported since the two pages don't currently share a constants module.
const CURRENCY_SUGGESTIONS = [
  'SAR', 'AED', 'EGP', 'KWD', 'QAR', 'BHD', 'OMR', 'JOD',
  'USD', 'EUR', 'GBP', 'CHF', 'JPY', 'CNY', 'INR', 'TRY',
]

function ExchangeRatesTab({ tenderId, onError, onSuccess }) {
  const [rates, setRates] = useState(null)
  const [fromCurrency, setFromCurrency] = useState('')
  const [toCurrency, setToCurrency] = useState('')
  const [rate, setRate] = useState('')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editRate, setEditRate] = useState('')
  const [editEffectiveDate, setEditEffectiveDate] = useState('')

  // Tender-scoped: explicit filter, never left to RLS alone for page-
  // level scoping. These are the FX assumptions THIS tender's own
  // pricing calculations resolve against -- not shared market data.
  const load = useCallback(async () => {
    if (!tenderId) return
    const { data, error } = await supabase.from('exchange_rate').select('*').eq('tender_id', tenderId).order('effective_date', { ascending: false })
    if (error) return onError(error.message)
    setRates(data)
  }, [tenderId, onError])

  useEffect(() => { setRates(null); load() }, [tenderId, load])

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    if (fromCurrency === toCurrency) { onError('From Currency and To Currency must be different.'); return }
    // Tender-scoped RPC -- not a direct INSERT. create_exchange_rate()
    // re-validates rate > 0 and from != to server-side; the same
    // temporal non-overlap behavior as resource_rate applies via the
    // table's existing constraints.
    const { error } = await supabase.rpc('create_exchange_rate', {
      p_tender_id: tenderId,
      p_from_currency: fromCurrency,
      p_to_currency: toCurrency,
      p_rate: Number(rate),
      p_effective_date: effectiveDate,
    })
    if (error) onError(`Could not save exchange rate — ${error.message}`)
    else {
      onSuccess('Exchange rate saved.')
      setFromCurrency(''); setToCurrency(''); setRate(''); setEffectiveDate('')
      await load()
    }
  }

  function startEdit(r) {
    setEditingId(r.id)
    setEditRate(String(r.rate))
    setEditEffectiveDate(r.effective_date)
  }

  async function handleSaveEdit(rateId) {
    onError(null)
    // Direct UPDATE remains RLS-protected (tender_id match) -- no
    // ownership field changes here (tender_id itself is never touched
    // by this edit), so no dedicated edit RPC is required for this
    // table, unlike resource_rate's temporal-rebuild case.
    const { error } = await supabase.from('exchange_rate')
      .update({ rate: Number(editRate), effective_date: editEffectiveDate })
      .eq('id', rateId)
    if (error) onError(`Could not update exchange rate — ${error.message}`)
    else {
      onSuccess('Exchange rate updated.')
      setEditingId(null)
      await load()
    }
  }

  async function handleDelete(rateId) {
    onError(null)
    const { error } = await supabase.from('exchange_rate').delete().eq('id', rateId)
    if (error) onError(`Could not delete exchange rate — ${error.message}`)
    else {
      onSuccess('Exchange rate deleted.')
      await load()
    }
  }

  return (
    <div>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="From Currency">
          <input
            list="currency-suggestions-from"
            value={fromCurrency}
            onChange={(e) => setFromCurrency(e.target.value)}
            onBlur={(e) => setFromCurrency(e.target.value.toUpperCase())}
            required
            placeholder="e.g. EUR"
            style={{ width: 120, padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13 }}
          />
          <datalist id="currency-suggestions-from">
            {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="To Currency">
          <input
            list="currency-suggestions-to"
            value={toCurrency}
            onChange={(e) => setToCurrency(e.target.value)}
            onBlur={(e) => setToCurrency(e.target.value.toUpperCase())}
            required
            placeholder="e.g. SAR"
            style={{ width: 120, padding: '6px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 13 }}
          />
          <datalist id="currency-suggestions-to">
            {CURRENCY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Rate"><Input type="number" step="any" min="0" value={rate} onChange={(e) => setRate(e.target.value)} required style={{ width: 110 }} /></Field>
        <Field label="Effective Date"><Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} required style={{ width: 160 }} /></Field>
        <Button type="submit">+ Add Exchange Rate</Button>
      </form>
      <p style={{ fontSize: 12, color: colors.textMuted, marginTop: -10, marginBottom: 16 }}>
        Applies to every Tender priced in {toCurrency || 'the To Currency'} with resources in {fromCurrency || 'the From Currency'} — resolved per-line against each Revision's own Pricing Date. One rate per From/To/Date combination.
      </p>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {rates === null && <div style={{ padding: 16 }}><SkeletonRows rows={3} cols={4} /></div>}
        {rates?.length === 0 && <div style={{ padding: 24 }}><EmptyState title="No exchange rates yet" /></div>}
        {rates?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th>From</Th><Th>To</Th><Th align="right">Rate</Th><Th>Effective Date</Th><Th></Th>
            </tr></thead>
            <tbody>
              {rates.map((r) => {
                const isEditing = editingId === r.id
                if (isEditing) {
                  return (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}`, background: '#FAFBFC' }}>
                      <Td>{r.from_currency}</Td>
                      <Td>{r.to_currency}</Td>
                      <Td align="right"><Input type="number" step="any" min="0" value={editRate} onChange={(e) => setEditRate(e.target.value)} style={{ width: 90, fontSize: 13 }} /></Td>
                      <Td><Input type="date" value={editEffectiveDate} onChange={(e) => setEditEffectiveDate(e.target.value)} style={{ width: 150, fontSize: 13 }} /></Td>
                      <Td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => handleSaveEdit(r.id)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Save</button>
                          <button onClick={() => setEditingId(null)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                        </div>
                      </Td>
                    </tr>
                  )
                }
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td>{r.from_currency}</Td>
                    <Td>{r.to_currency}</Td>
                    <Td align="right">{formatNumber(r.rate)}</Td>
                    <Td>{r.effective_date}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => startEdit(r)} style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                        <button onClick={() => handleDelete(r.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                      </div>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// =======================================================================
// COSTING PROFILES -- how a resource is costed once it is a crew member.
// A profile belongs to exactly one tender and points at one of that
// tender's resources. The three costing methods are the ones
// resolve_crew_cost_per_shift_v2() already understands:
//   time_based        rate x quantity x (shifts per time unit)
//   equipment_hourly  rate x quantity x operating hours x utilisation
//   production_based  rate x quantity x output per shift
// Creation goes through create_costing_profile(), which re-checks the
// tender, the FirstPrinciples.Manage permission, that the resource belongs
// to THIS tender, and that the fields match the chosen method. Existing
// profiles are only listed here -- changing one would silently change the
// cost of every crew that uses it.
// =======================================================================
const COSTING_METHODS = [
  { value: 'time_based', label: 'Time-based' },
  { value: 'equipment_hourly', label: 'Equipment hourly' },
  { value: 'production_based', label: 'Production-based' },
]
const COSTING_TIME_UNITS = [
  { value: 'hour', label: 'Hour' },
  { value: 'shift', label: 'Shift' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
]

// One-line summary of a profile, used in the list below and in the Crews
// member dropdown so two profiles of the same resource can be told apart.
function describeCostingProfile(p) {
  if (!p) return ''
  if (p.cost_basis === 'time_based') return `Time-based, per ${p.time_unit || '?'}`
  if (p.cost_basis === 'equipment_hourly') {
    return `Equipment hourly, ${formatNumber(p.operating_hours_per_shift)} h/shift @ ${formatNumber(p.utilization_pct ?? 100)}%`
  }
  if (p.cost_basis === 'production_based') return `Production-based, ${formatNumber(p.production_rate_per_shift)} per shift`
  return p.cost_basis || ''
}

function CostingProfilesTab({ tenderId, onError, onSuccess }) {
  const [resources, setResources] = useState([])
  const [profiles, setProfiles] = useState(null)
  const [resourceId, setResourceId] = useState('')
  const [costBasis, setCostBasis] = useState('')
  const [timeUnit, setTimeUnit] = useState('')
  const [operatingHours, setOperatingHours] = useState('')
  const [utilizationPct, setUtilizationPct] = useState('100')
  const [productionRate, setProductionRate] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  // Tender-scoped reads: only this tender's active resources, and only this
  // tender's profiles -- never left to RLS alone for page-level scoping.
  const load = useCallback(async () => {
    if (!tenderId) return
    const [resRes, profRes] = await Promise.all([
      supabase.from('resource_library').select('id, code, description, resource_type, unit').eq('is_active', true).eq('tender_id', tenderId).order('code'),
      supabase.from('resource_costing_profile')
        .select('id, resource_library_id, cost_basis, time_unit, operating_hours_per_shift, utilization_pct, production_rate_per_shift, notes, created_at, resource_library(code, description)')
        .eq('tender_id', tenderId).order('created_at', { ascending: false }),
    ])
    if (resRes.error) return onError(resRes.error.message)
    if (profRes.error) return onError(profRes.error.message)
    setResources(resRes.data)
    setProfiles(profRes.data)
  }, [tenderId, onError])

  useEffect(() => {
    // Clear immediately on a Tender switch so a stale Resource selection
    // from the previous Tender can never be submitted.
    setResources([]); setProfiles(null); setResourceId('')
    load()
  }, [tenderId, load])

  function resetForm() {
    setResourceId(''); setCostBasis(''); setTimeUnit(''); setOperatingHours('')
    setUtilizationPct('100'); setProductionRate(''); setNotes('')
  }

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    setSaving(true)
    // Only the fields that belong to the chosen method are sent; the rest
    // stay NULL, exactly like the existing profiles.
    const { error } = await supabase.rpc('create_costing_profile', {
      p_tender_id: tenderId,
      p_resource_library_id: resourceId,
      p_cost_basis: costBasis,
      p_time_unit: costBasis === 'time_based' ? timeUnit : null,
      p_operating_hours_per_shift: costBasis === 'equipment_hourly' ? Number(operatingHours) : null,
      p_utilization_pct: costBasis === 'equipment_hourly' && utilizationPct !== '' ? Number(utilizationPct) : null,
      p_production_rate_per_shift: costBasis === 'production_based' ? Number(productionRate) : null,
      p_notes: notes.trim() || null,
    })
    setSaving(false)
    if (error) onError(`Could not save costing profile — ${error.message}`)
    else {
      onSuccess('Costing profile saved. It is now available when you add a crew member.')
      resetForm()
      await load()
    }
  }

  return (
    <div>
      <p style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16 }}>
        A costing profile says how one of this tender's resources is costed when it is a crew member: by time
        (per hour, shift, day or week), as equipment by the hour with a utilisation, or by what it produces
        per shift. The profile is only the method — the rate itself comes from Resource Rates. Profiles belong
        to this tender only.
      </p>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Resource">
          <Select value={resourceId} onChange={(e) => setResourceId(e.target.value)} required disabled={resources.length === 0} style={{ width: 260 }}>
            <option value="" disabled>{resources.length === 0 ? 'No resources in this tender yet' : 'Select…'}</option>
            {resources.map((r) => <option key={r.id} value={r.id}>{r.code} — {r.description} ({r.resource_type})</option>)}
          </Select>
        </Field>
        <Field label="Costing Method">
          <Select value={costBasis} onChange={(e) => setCostBasis(e.target.value)} required style={{ width: 190 }}>
            <option value="" disabled>Select…</option>
            {COSTING_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
        </Field>
        {costBasis === 'time_based' && (
          <Field label="Time Unit">
            <Select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)} required style={{ width: 130 }}>
              <option value="" disabled>Select…</option>
              {COSTING_TIME_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </Select>
          </Field>
        )}
        {costBasis === 'equipment_hourly' && (
          <>
            <Field label="Operating Hours / Shift">
              <Input type="number" step="any" min="0" value={operatingHours} onChange={(e) => setOperatingHours(e.target.value)} required style={{ width: 150 }} />
            </Field>
            <Field label="Utilization %">
              <Input type="number" step="any" min="0" max="100" value={utilizationPct} onChange={(e) => setUtilizationPct(e.target.value)} style={{ width: 110 }} />
            </Field>
          </>
        )}
        {costBasis === 'production_based' && (
          <Field label="Output / Shift">
            <Input type="number" step="any" min="0" value={productionRate} onChange={(e) => setProductionRate(e.target.value)} required style={{ width: 130 }} />
          </Field>
        )}
        <Field label="Notes">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" style={{ width: 180 }} />
        </Field>
        <Button type="submit" disabled={saving || !costBasis}>{saving ? 'Saving…' : '+ Add Costing Profile'}</Button>
      </form>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {profiles === null && <div style={{ padding: 16 }}><SkeletonRows rows={3} cols={3} /></div>}
        {profiles?.length === 0 && <div style={{ padding: 24 }}><EmptyState title="No costing profiles yet for this tender" description="Add one above, then pick it when you add a member to a crew." /></div>}
        {profiles?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
              <Th>Resource</Th><Th>Costing Method</Th><Th>Notes</Th>
            </tr></thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Td>{p.resource_library?.code} — {p.resource_library?.description}</Td>
                  <Td>{describeCostingProfile(p)}</Td>
                  <Td muted>{p.notes || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function CrewsTab({ tenderId, onError, onSuccess }) {
  const [crews, setCrews] = useState(null)
  const [calendars, setCalendars] = useState([])
  const [resources, setResources] = useState([])
  const [expandedCrew, setExpandedCrew] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [calendarId, setCalendarId] = useState('')

  const load = useCallback(async () => {
    const [crewRes, calRes, resRes] = await Promise.all([
      supabase.from('crew').select('*, project_calendar(name)').eq('tender_id', tenderId).order('created_at'),
      supabase.from('project_calendar').select('id, name').eq('tender_id', tenderId),
      // crew_member stores resource_costing_profile_id (not resource_library_id
      // directly) — a resource's costing method (time_based/equipment_hourly/
      // production_based) is defined per-profile. Load profiles joined with
      // their resource_library row so the dropdown can still show the
      // familiar code/description, while persisting the correct profile id.
      // Tender-scoped: same tenderId already used for crew/calendar
      // two lines above -- resource_costing_profile needed the
      // identical filter (missed in the first Resource Consumer
      // Audit, caught here on re-audit).
      supabase.from('resource_costing_profile').select('id, resource_library_id, cost_basis, time_unit, operating_hours_per_shift, utilization_pct, production_rate_per_shift, resource_library(id, code, description, resource_type, is_active)').eq('tender_id', tenderId).order('id'),
    ])
    if (crewRes.error) return onError(crewRes.error.message)
    if (calRes.error) return onError(calRes.error.message)
    if (resRes.error) return onError(resRes.error.message)
    setCrews(crewRes.data)
    setCalendars(calRes.data)
    // Filter out profiles whose underlying resource was deactivated, mirroring
    // the previous .eq('is_active', true) filter on resource_library.
    setResources((resRes.data || []).filter((p) => p.resource_library?.is_active))
  }, [tenderId, onError])

  useEffect(() => { load() }, [load])

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    const { error } = await supabase.from('crew').insert({ tender_id: tenderId, name, description: description || null, project_calendar_id: calendarId })
    if (error) onError(error.message)
    else { onSuccess(`Crew "${name}" created.`); setName(''); setDescription(''); await load() }
  }

  return (
    <div>
      {calendars.length === 0 && (
        <div style={{ padding: '10px 14px', background: colors.warningBg, color: colors.warningText, borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          ⚠ Create a Calendar first — a crew requires one.
        </div>
      )}

      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Crew Name"><Input value={name} onChange={(e) => setName(e.target.value)} required style={{ width: 200 }} placeholder="Formwork Gang A" /></Field>
        <Field label="Description"><Input value={description} onChange={(e) => setDescription(e.target.value)} style={{ width: 240 }} /></Field>
        <Field label="Calendar">
          <Select value={calendarId} onChange={(e) => setCalendarId(e.target.value)} required disabled={calendars.length === 0} style={{ width: 200 }}>
            <option value="" disabled>Select calendar…</option>
            {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Button type="submit" disabled={calendars.length === 0}>+ Add Crew</Button>
      </form>

      {crews === null && <SkeletonRows rows={2} cols={3} />}
      {crews?.length === 0 && <EmptyState title="No crews yet" />}

      {crews?.map((crew) => (
        <CrewCard
          key={crew.id}
          crew={crew}
          resources={resources}
          expanded={expandedCrew === crew.id}
          onToggle={() => setExpandedCrew(expandedCrew === crew.id ? null : crew.id)}
          onError={onError}
          onSuccess={onSuccess}
        />
      ))}
    </div>
  )
}

function CrewCard({ crew, resources, expanded, onToggle, onError, onSuccess }) {
  const [members, setMembers] = useState(null)
  const [resourceProfileId, setResourceProfileId] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [costPerShift, setCostPerShift] = useState(null)

  const loadMembers = useCallback(async () => {
    const { data, error } = await supabase.from('crew_member').select('*').eq('crew_id', crew.id).order('created_at')
    if (error) return onError(error.message)
    setMembers(data)

    // Live cost preview: calls the existing DB function directly, using
    // today's date as a preview basis. The actual rate used in a real
    // BOQ line is always resolved from that revision's pricing_date via
    // first_principles_rate_breakdown, not from this preview call.
    // Gate-2-prep fix: v1 (resolve_crew_cost_per_shift) does not exist on
    // the target database -- v2 is the correct, already-existing function
    // (it predates this integration, present on both old and new DBs) and
    // additionally reports has_missing_rate / missing_rate_resource_codes.
    // No calculation logic changed here -- only the RPC name and reading
    // the two extra fields v2 already returns.
    const { data: costData, error: costErr } = await supabase.rpc('resolve_crew_cost_per_shift_v2', {
      p_crew_id: crew.id, p_pricing_date: new Date().toISOString().slice(0, 10),
    })
    if (!costErr && costData?.[0]) setCostPerShift(costData[0])
  }, [crew.id, onError])

  useEffect(() => { if (expanded) loadMembers() }, [expanded, loadMembers])

  async function handleAddMember(e) {
    e.preventDefault()
    onError(null)
    // crew_member.resource_costing_profile_id is the actual FK column (not
    // resource_library_id) -- resourceProfileId already holds a
    // resource_costing_profile.id, selected from the profile-joined list.
    const { error } = await supabase.from('crew_member').insert({ crew_id: crew.id, resource_costing_profile_id: resourceProfileId, quantity: Number(quantity) })
    if (error) onError(error.message)
    else { onSuccess('Crew member added.'); setResourceProfileId(''); setQuantity('1'); await loadMembers() }
  }

  async function handleRemoveMember(memberId) {
    onError(null)
    const { error } = await supabase.from('crew_member').delete().eq('id', memberId)
    if (error) onError(error.message)
    else { onSuccess('Crew member removed.'); await loadMembers() }
  }

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      <button onClick={onToggle} style={{ width: '100%', textAlign: 'left', padding: 14, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 12, color: colors.textMuted, marginRight: 6 }}>{expanded ? '▾' : '▸'}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{crew.name}</span>
          <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 8 }}>Calendar: {crew.project_calendar?.name}</span>
        </div>
        {costPerShift && <span style={{ fontSize: 13, fontWeight: 600, color: colors.primary }}>{formatNumber(costPerShift.total_cost_per_shift)} SAR/shift</span>}
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px' }}>
          <form onSubmit={handleAddMember} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
            <Field label="Resource">
              <Select value={resourceProfileId} onChange={(e) => setResourceProfileId(e.target.value)} required style={{ width: 340, fontSize: 13 }}>
                <option value="" disabled>Select…</option>
                {resources.map((p) => <option key={p.id} value={p.id}>{p.resource_library?.code} — {p.resource_library?.description} ({describeCostingProfile(p)})</option>)}
              </Select>
            </Field>
            <Field label="Quantity"><Input type="number" step="any" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} required style={{ width: 80, fontSize: 13 }} /></Field>
            <Button type="submit" size="sm">+ Add</Button>
          </form>

          {members === null && <SkeletonRows rows={2} cols={2} />}
          {members?.length === 0 && <div style={{ fontSize: 13, color: colors.textMuted }}>No members yet.</div>}
          {members?.map((m) => {
            const profile = resources.find((p) => p.id === m.resource_costing_profile_id)
            return (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 13 }}>
                <span>{profile ? `${profile.resource_library?.code} — ${profile.resource_library?.description}` : m.resource_costing_profile_id} × {formatNumber(m.quantity)}</span>
                <button onClick={() => handleRemoveMember(m.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Remove</button>
              </div>
            )
          })}

          {costPerShift && (
            <div style={{ marginTop: 10, padding: 10, background: '#FAFBFC', borderRadius: 6, fontSize: 12, color: colors.textSecondary }}>
              Preview (today's rates): {formatNumber(costPerShift.total_man_hours_per_shift)} man-hours/shift · {formatNumber(costPerShift.total_cost_per_shift)} SAR/shift
              {costPerShift.has_missing_rate && (
                <div style={{ color: colors.danger, marginTop: 4 }}>
                  ⚠ Missing rate for: {(costPerShift.missing_rate_resource_codes || []).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// =======================================================================
// PRODUCTIVITY LIBRARY — labour/equipment output standards + factors
// =======================================================================
function ProductivityTab({ tenderId, onError, onSuccess }) {
  const [items, setItems] = useState(null)
  const [crews, setCrews] = useState([])
  const [expandedId, setExpandedId] = useState(null)

  const [activityDescription, setActivityDescription] = useState('')
  const [unit, setUnit] = useState('')
  const [domain, setDomain] = useState('labour')
  const [basis, setBasis] = useState('crew_output_per_shift')
  const [manHoursPerUnit, setManHoursPerUnit] = useState('')
  const [crewOutputPerShift, setCrewOutputPerShift] = useState('')
  const [referenceCrewId, setReferenceCrewId] = useState('')
  const [equipmentUtilizationPct, setEquipmentUtilizationPct] = useState('')
  const [source, setSource] = useState('')

  const load = useCallback(async () => {
    const [plRes, crewRes] = await Promise.all([
      // Tender-scoped: productivity_library is one of the seven
      // Tender-owned entities on the backend -- caught missing its
      // filter on re-audit, same pattern as resource_costing_profile
      // above.
      supabase.from('productivity_library').select('*').eq('is_active', true).eq('tender_id', tenderId).order('created_at'),
      supabase.from('crew').select('id, name').eq('tender_id', tenderId),
    ])
    if (plRes.error) return onError(plRes.error.message)
    if (crewRes.error) return onError(crewRes.error.message)
    setItems(plRes.data)
    setCrews(crewRes.data)
  }, [tenderId, onError])

  useEffect(() => { load() }, [load])

  // basis and domain are constrained together by DB CHECK constraints:
  // equipment domain cannot use man_hours_per_unit basis. Mirror that
  // here only to guide the form — the DB is still the enforcement point.
  useEffect(() => { if (domain === 'equipment' && basis === 'man_hours_per_unit') setBasis('crew_output_per_shift') }, [domain, basis])

  async function handleAdd(e) {
    e.preventDefault()
    onError(null)
    const payload = {
      tender_id: tenderId,
      activity_description: activityDescription, unit, productivity_domain: domain, basis,
      source: source || null,
    }
    if (basis === 'man_hours_per_unit') payload.man_hours_per_unit = Number(manHoursPerUnit)
    else {
      payload.crew_output_per_shift = Number(crewOutputPerShift)
      payload.reference_crew_id = referenceCrewId
    }
    if (domain === 'equipment' && equipmentUtilizationPct) payload.equipment_utilization_pct = Number(equipmentUtilizationPct)

    const { error } = await supabase.from('productivity_library').insert(payload)
    if (error) onError(error.message)
    else {
      onSuccess(`Productivity standard "${activityDescription}" created.`)
      setActivityDescription(''); setUnit(''); setManHoursPerUnit(''); setCrewOutputPerShift(''); setReferenceCrewId(''); setEquipmentUtilizationPct(''); setSource('')
      await load()
    }
  }

  return (
    <div>
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Activity"><Input value={activityDescription} onChange={(e) => setActivityDescription(e.target.value)} required style={{ width: 220 }} placeholder="Formwork - Slab Soffit" /></Field>
        <Field label="Unit"><Input value={unit} onChange={(e) => setUnit(e.target.value)} required style={{ width: 80 }} placeholder="m2" /></Field>
        <Field label="Domain">
          <Select value={domain} onChange={(e) => setDomain(e.target.value)} style={{ width: 120 }}>
            <option value="labour">Labour</option>
            <option value="equipment">Equipment</option>
          </Select>
        </Field>
        <Field label="Basis">
          <Select value={basis} onChange={(e) => setBasis(e.target.value)} style={{ width: 190 }}>
            <option value="crew_output_per_shift">Crew Output/Shift</option>
            <option value="man_hours_per_unit" disabled={domain === 'equipment'}>Man-Hours/Unit</option>
          </Select>
        </Field>

        {basis === 'man_hours_per_unit' ? (
          <Field label="Man-Hours/Unit"><Input type="number" step="any" min="0" value={manHoursPerUnit} onChange={(e) => setManHoursPerUnit(e.target.value)} required style={{ width: 130 }} /></Field>
        ) : (
          <>
            <Field label="Output/Shift"><Input type="number" step="any" min="0" value={crewOutputPerShift} onChange={(e) => setCrewOutputPerShift(e.target.value)} required style={{ width: 120 }} /></Field>
            <Field label="Reference Crew">
              <Select value={referenceCrewId} onChange={(e) => setReferenceCrewId(e.target.value)} required style={{ width: 200 }}>
                <option value="" disabled>Select crew…</option>
                {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          </>
        )}

        {domain === 'equipment' && (
          <Field label="Utilization %"><Input type="number" step="any" min="0" max="100" value={equipmentUtilizationPct} onChange={(e) => setEquipmentUtilizationPct(e.target.value)} style={{ width: 120 }} /></Field>
        )}

        <Field label="Source"><Input value={source} onChange={(e) => setSource(e.target.value)} style={{ width: 180 }} placeholder="Company historical..." /></Field>
        <Button type="submit">+ Add Standard</Button>
      </form>

      {items === null && <SkeletonRows rows={2} cols={3} />}
      {items?.length === 0 && <EmptyState title="No productivity standards yet" />}

      {items?.map((item) => (
        <ProductivityCard
          key={item.id}
          tenderId={tenderId}
          item={item}
          expanded={expandedId === item.id}
          onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
          onError={onError}
          onSuccess={onSuccess}
        />
      ))}
    </div>
  )
}

function ProductivityCard({ tenderId, item, expanded, onToggle, onError, onSuccess }) {
  const [factors, setFactors] = useState(null)
  const [factorName, setFactorName] = useState('')
  const [adjustmentPct, setAdjustmentPct] = useState('')

  const loadFactors = useCallback(async () => {
    const { data, error } = await supabase.from('productivity_factor').select('*').eq('productivity_library_id', item.id).order('created_at')
    if (error) return onError(error.message)
    setFactors(data)
  }, [item.id, onError])

  useEffect(() => { if (expanded) loadFactors() }, [expanded, loadFactors])

  async function handleAddFactor(e) {
    e.preventDefault()
    onError(null)
    // productivity_factor is one of the seven Tender-owned entities on
    // the backend (mandatory tender_id, no more optional override) --
    // same explicit field as productivity_library's own insert above.
    const { error } = await supabase.from('productivity_factor').insert({ tender_id: tenderId, productivity_library_id: item.id, factor_name: factorName, adjustment_pct: Number(adjustmentPct) })
    if (error) onError(error.message)
    else { onSuccess('Factor added.'); setFactorName(''); setAdjustmentPct(''); await loadFactors() }
  }

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
      <button onClick={onToggle} style={{ width: '100%', textAlign: 'left', padding: 14, border: 'none', background: 'none', cursor: 'pointer' }}>
        <span style={{ fontSize: 12, color: colors.textMuted, marginRight: 6 }}>{expanded ? '▾' : '▸'}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>{item.activity_description}</span>
        <span style={{ fontSize: 12, color: colors.textMuted, marginLeft: 8 }}>
          {item.basis === 'man_hours_per_unit' ? `${formatNumber(item.man_hours_per_unit)} hr/${item.unit}` : `${formatNumber(item.crew_output_per_shift)} ${item.unit}/shift`}
          {' · '}{item.productivity_domain}
          {item.equipment_utilization_pct && ` · ${item.equipment_utilization_pct}% utilization`}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px' }}>
          <form onSubmit={handleAddFactor} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
            <Field label="Factor Name"><Input value={factorName} onChange={(e) => setFactorName(e.target.value)} required style={{ width: 220, fontSize: 13 }} placeholder="Working at height >20m" /></Field>
            <Field label="Adjustment %"><Input type="number" step="any" value={adjustmentPct} onChange={(e) => setAdjustmentPct(e.target.value)} required style={{ width: 110, fontSize: 13 }} placeholder="-15" /></Field>
            <Button type="submit" size="sm">+ Add Factor</Button>
          </form>

          {factors === null && <SkeletonRows rows={1} cols={2} />}
          {factors?.length === 0 && <div style={{ fontSize: 13, color: colors.textMuted }}>No adjustment factors yet.</div>}
          {factors?.map((f) => (
            <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${colors.border}`, fontSize: 13 }}>
              <span>{f.factor_name}</span>
              <span style={{ color: f.adjustment_pct < 0 ? colors.danger : colors.accentGreen, fontWeight: 600 }}>{f.adjustment_pct > 0 ? '+' : ''}{f.adjustment_pct}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}
      {children}
    </label>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted }) {
  return <td style={{ textAlign: align, padding: '11px 16px', color: muted ? colors.textMuted : colors.textPrimary, fontSize: 14 }}>{children}</td>
}
